import { useState, useEffect, useCallback } from 'react';
import { Plus, ExternalLink, Eye, EyeOff, Copy, Trash2, Edit3, X, Check, Package, DollarSign, RefreshCw, Link2, Key, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductsTab = ({ token, onLogout }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', username: '', password: '', notes: '', pricing: [], api_url: '', api_key: '' });
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/products`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) { onLogout(); return; }
      if (res.ok) setProducts(await res.json());
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const resetForm = () => {
    setForm({ name: '', url: '', username: '', password: '', notes: '', pricing: [], api_url: '', api_key: '' });
    setEditId(null);
    setShowForm(false);
  };

  const startEdit = (p) => {
    setForm({ name: p.name, url: p.url, username: p.username, password: p.password, notes: p.notes, pricing: p.pricing || [], api_url: p.api_url || '', api_key: p.api_key || '' });
    setEditId(p.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      const url = editId ? `${API}/api/admin/products/${editId}` : `${API}/api/admin/products`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (res.ok) { fetchProducts(); resetForm(); }
    } catch {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    try {
      await fetch(`${API}/api/admin/products/${id}`, { method: 'DELETE', headers });
      fetchProducts();
    } catch {}
  };

  const addPricing = () => {
    setForm(f => ({ ...f, pricing: [...f.pricing, { plan: '', price: '', interval: 'monatlich' }] }));
  };
  const updatePricing = (i, key, val) => {
    setForm(f => ({ ...f, pricing: f.pricing.map((p, j) => j === i ? { ...p, [key]: val } : p) }));
  };
  const removePricing = (i) => {
    setForm(f => ({ ...f, pricing: f.pricing.filter((_, j) => j !== i) }));
  };

  if (loading) return <div className="p-8 text-center text-gray-600 text-sm">Lade Produkte...</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Space_Grotesk'] font-bold text-white text-lg">Produkte & Zugänge</h2>
          <p className="text-gray-500 text-xs mt-0.5">Verwaltung aller Produktzugänge und Abo-Modelle</p>
        </div>
        <button
          data-testid="add-product-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold transition-colors"
        >
          <Plus className="w-4 h-4" /> Neues Produkt
        </button>
      </div>

      {/* Product Form Modal */}
      {showForm && (
        <div className="mb-6 rounded-2xl border border-white/10 p-6" style={{ background: 'rgba(255,255,255,0.03)' }}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-white font-semibold text-sm">{editId ? 'Produkt bearbeiten' : 'Neues Produkt anlegen'}</h3>
            <button onClick={resetForm} className="text-gray-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Produktname *</label>
              <input data-testid="product-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none" placeholder="z.B. Lazy Leads" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">URL</label>
              <input data-testid="product-url" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none" placeholder="https://lazyleads.laizyones.de" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Benutzername</label>
                <input data-testid="product-username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none" placeholder="admin@laizyones.de" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Passwort</label>
                <input data-testid="product-password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none" placeholder="Passwort" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Notizen</label>
              <textarea data-testid="product-notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none resize-none" placeholder="Interne Notizen..." />
            </div>

            {/* API Connection */}
            <div className="border-t border-white/5 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Link2 className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-cyan-400 font-medium">API-Verbindung (für Live-Preise)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">API-URL</label>
                  <input data-testid="product-api-url" value={form.api_url} onChange={e => setForm(f => ({ ...f, api_url: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-cyan-500/50 focus:outline-none" placeholder="https://lazyleads.laizyones.de" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">API-Key</label>
                  <input data-testid="product-api-key" value={form.api_key} onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-600 focus:border-cyan-500/50 focus:outline-none" placeholder="API-Schlüssel" />
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-500">Preise / Abo-Modelle</label>
                <button onClick={addPricing} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"><Plus className="w-3 h-3" /> Preis hinzufügen</button>
              </div>
              {form.pricing.map((p, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input value={p.plan} onChange={e => updatePricing(i, 'plan', e.target.value)} className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none" placeholder="Plan (z.B. Starter)" />
                  <input value={p.price} onChange={e => updatePricing(i, 'price', e.target.value)} className="w-24 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs placeholder-gray-600 focus:border-emerald-500/50 focus:outline-none" placeholder="Preis" />
                  <select value={p.interval} onChange={e => updatePricing(i, 'interval', e.target.value)} className="px-2 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:border-emerald-500/50 focus:outline-none">
                    <option value="monatlich">monatlich</option>
                    <option value="jaehrlich">jährlich</option>
                    <option value="einmalig">einmalig</option>
                  </select>
                  <button onClick={() => removePricing(i)} className="text-gray-600 hover:text-red-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>

            <button data-testid="save-product-btn" onClick={handleSave} disabled={saving || !form.name} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-white text-sm font-semibold transition-colors">
              <Check className="w-4 h-4" /> {saving ? 'Speichern...' : editId ? 'Aktualisieren' : 'Anlegen'}
            </button>
          </div>
        </div>
      )}

      {/* Product Cards Grid */}
      {products.length === 0 ? (
        <div className="text-center py-16">
          <Package className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Noch keine Produkte angelegt</p>
          <p className="text-gray-600 text-xs mt-1">Klicke "Neues Produkt" um zu starten</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map(p => <ProductCard key={p.id} product={p} onEdit={startEdit} onDelete={handleDelete} token={token} />)}
        </div>
      )}
    </div>
  );
};

const ProductCard = ({ product, onEdit, onDelete, token }) => {
  const [showPass, setShowPass] = useState(false);
  const [copied, setCopied] = useState('');
  const [remotePricing, setRemotePricing] = useState(null);
  const [loadingRemote, setLoadingRemote] = useState(false);
  const [remoteError, setRemoteError] = useState('');
  const [editingRemote, setEditingRemote] = useState(false);
  const [editPlans, setEditPlans] = useState([]);
  const [savingRemote, setSavingRemote] = useState(false);

  const copy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 1500);
  };

  const fetchRemotePricing = async () => {
    setLoadingRemote(true);
    setRemoteError('');
    try {
      const res = await fetch(`${API}/api/admin/products/${product.id}/remote-pricing`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.status === 'success') {
        setRemotePricing(data.data);
        setEditPlans(JSON.parse(JSON.stringify(data.data.plans || [])));
      } else {
        setRemoteError(data.detail || 'Fehler beim Abrufen');
      }
    } catch (e) {
      setRemoteError(e.message);
    }
    setLoadingRemote(false);
  };

  const saveRemotePricing = async () => {
    setSavingRemote(true);
    setRemoteError('');
    try {
      const res = await fetch(`${API}/api/admin/products/${product.id}/remote-pricing`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plans: editPlans }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setRemotePricing(data.data);
        setEditPlans(JSON.parse(JSON.stringify(data.data.plans || [])));
        setEditingRemote(false);
      } else {
        setRemoteError(data.detail || 'Fehler beim Speichern');
      }
    } catch (e) {
      setRemoteError(e.message);
    }
    setSavingRemote(false);
  };

  const updatePlan = (i, key, val) => {
    setEditPlans(plans => plans.map((p, j) => j === i ? { ...p, [key]: val } : p));
  };
  const updateFeature = (planIdx, featIdx, val) => {
    setEditPlans(plans => plans.map((p, j) => j === planIdx ? { ...p, features: p.features.map((f, k) => k === featIdx ? val : f) } : p));
  };
  const addFeature = (planIdx) => {
    setEditPlans(plans => plans.map((p, j) => j === planIdx ? { ...p, features: [...(p.features || []), ''] } : p));
  };
  const removeFeature = (planIdx, featIdx) => {
    setEditPlans(plans => plans.map((p, j) => j === planIdx ? { ...p, features: (p.features || []).filter((_, k) => k !== featIdx) } : p));
  };

  const hasApi = !!product.api_url;

  return (
    <div data-testid={`product-card-${product.id}`} className="rounded-2xl border border-white/10 p-5 transition-all hover:border-emerald-500/20" style={{ background: 'rgba(255,255,255,0.03)' }}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <Package className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">{product.name}</h3>
            {product.url && (
              <a href={product.url} target="_blank" rel="noopener noreferrer" className="text-emerald-400 text-xs flex items-center gap-1 hover:text-emerald-300 mt-0.5">
                <ExternalLink className="w-3 h-3" /> {product.url.replace(/^https?:\/\//, '').slice(0, 30)}
              </a>
            )}
            {hasApi && (
              <span className="text-cyan-400 text-[10px] flex items-center gap-1 mt-0.5">
                <Link2 className="w-2.5 h-2.5" /> API verbunden
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid={`edit-product-${product.id}`} onClick={() => onEdit(product)} className="p-1.5 rounded-lg text-gray-600 hover:text-white hover:bg-white/5 transition-colors">
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button data-testid={`delete-product-${product.id}`} onClick={() => onDelete(product.id)} className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-colors">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Credentials */}
      {(product.username || product.password) && (
        <div className="space-y-2 mb-4">
          {product.username && (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/5">
              <div className="min-w-0">
                <span className="text-gray-600 text-[10px] uppercase tracking-wider block">Benutzer</span>
                <span className="text-gray-300 text-xs truncate block">{product.username}</span>
              </div>
              <button onClick={() => copy(product.username, `user-${product.id}`)} className="text-gray-600 hover:text-emerald-400 transition-colors flex-shrink-0 ml-2">
                {copied === `user-${product.id}` ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
          {product.password && (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 border border-white/5">
              <div className="min-w-0">
                <span className="text-gray-600 text-[10px] uppercase tracking-wider block">Passwort</span>
                <span className="text-gray-300 text-xs truncate block font-mono">{showPass ? product.password : '\u2022'.repeat(Math.min(product.password.length, 12))}</span>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <button onClick={() => setShowPass(!showPass)} className="text-gray-600 hover:text-white transition-colors">
                  {showPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => copy(product.password, `pass-${product.id}`)} className="text-gray-600 hover:text-emerald-400 transition-colors">
                  {copied === `pass-${product.id}` ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pricing */}
      {product.pricing && product.pricing.length > 0 && !hasApi && (
        <div className="mb-3">
          <div className="flex items-center gap-1.5 text-gray-500 text-[10px] uppercase tracking-wider mb-1.5">
            <DollarSign className="w-3 h-3" /> Lokale Preise
          </div>
          <div className="space-y-1">
            {product.pricing.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-400">{p.plan || 'Plan'}</span>
                <span className="text-white font-medium">{p.price}{p.price && '\u00A0\u20AC'} <span className="text-gray-600 font-normal">/ {p.interval}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Remote Pricing (API) */}
      {hasApi && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-cyan-400 text-[10px] uppercase tracking-wider">
              <Link2 className="w-3 h-3" /> Live-Preise
            </div>
            <button
              data-testid={`fetch-pricing-${product.id}`}
              onClick={fetchRemotePricing}
              disabled={loadingRemote}
              className="text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRemote ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {remoteError && <p className="text-red-400 text-xs mb-2">{remoteError}</p>}

          {remotePricing && !editingRemote && (
            <div className="space-y-1.5">
              {(remotePricing.plans || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
                  <div>
                    <span className="text-white font-medium">{p.name}</span>
                    {!p.active && <span className="text-gray-600 text-[10px] ml-1">(inaktiv)</span>}
                  </div>
                  <span className="text-cyan-300 font-medium">{p.price} {p.currency || '\u20AC'} <span className="text-gray-500 font-normal">/ {p.interval}</span></span>
                </div>
              ))}
              {remotePricing.last_updated && <p className="text-gray-600 text-[10px]">Aktualisiert: {new Date(remotePricing.last_updated).toLocaleString('de-DE')}</p>}
              <button
                data-testid={`edit-remote-pricing-${product.id}`}
                onClick={() => setEditingRemote(true)}
                className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 mt-1"
              >
                <Edit3 className="w-3 h-3" /> Preise bearbeiten
              </button>
            </div>
          )}

          {editingRemote && (
            <div className="space-y-3 p-3 rounded-xl border border-cyan-500/20 bg-cyan-500/5">
              {editPlans.map((plan, i) => (
                <div key={i} className="space-y-2 pb-3 border-b border-white/5 last:border-0 last:pb-0">
                  <div className="grid grid-cols-3 gap-2">
                    <input value={plan.name || ''} onChange={e => updatePlan(i, 'name', e.target.value)} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-cyan-500/50 focus:outline-none" placeholder="Plan" />
                    <input value={plan.price || ''} onChange={e => updatePlan(i, 'price', e.target.value)} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-cyan-500/50 focus:outline-none" placeholder="Preis" type="number" />
                    <select value={plan.interval || 'monatlich'} onChange={e => updatePlan(i, 'interval', e.target.value)} className="px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-xs focus:border-cyan-500/50 focus:outline-none">
                      <option value="monatlich">monatlich</option>
                      <option value="jaehrlich">jährlich</option>
                      <option value="einmalig">einmalig</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-gray-500 text-[10px]">Features:</span>
                    <button onClick={() => addFeature(i)} className="text-cyan-400 text-[10px] hover:text-cyan-300">+ Hinzufügen</button>
                  </div>
                  {(plan.features || []).map((f, fi) => (
                    <div key={fi} className="flex items-center gap-1">
                      <input value={f} onChange={e => updateFeature(i, fi, e.target.value)} className="flex-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white text-[11px] focus:border-cyan-500/50 focus:outline-none" />
                      <button onClick={() => removeFeature(i, fi)} className="text-gray-600 hover:text-red-400"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label className="flex items-center gap-2 text-xs text-gray-400 mt-1">
                    <input type="checkbox" checked={plan.active !== false} onChange={e => updatePlan(i, 'active', e.target.checked)} className="rounded" /> Aktiv
                  </label>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <button onClick={saveRemotePricing} disabled={savingRemote} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 disabled:opacity-40 text-white text-xs font-semibold transition-colors">
                  <Save className="w-3 h-3" /> {savingRemote ? 'Speichern...' : 'Speichern'}
                </button>
                <button onClick={() => setEditingRemote(false)} className="px-3 py-1.5 rounded-lg text-gray-500 text-xs hover:text-white transition-colors">Abbrechen</button>
              </div>
            </div>
          )}

          {!remotePricing && !loadingRemote && !remoteError && (
            <button onClick={fetchRemotePricing} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Preise laden
            </button>
          )}
        </div>
      )}

      {/* Notes */}
      {product.notes && (
        <p className="text-gray-500 text-xs leading-relaxed border-t border-white/5 pt-3 mt-3">{product.notes}</p>
      )}
    </div>
  );
};

export default ProductsTab;
